import { TimeUnit } from "src/constants";
import dayjs, { Dayjs } from "dayjs";
import i18n from "src/i18n";
import { getDayjsLocale } from "src/i18n/direction";
import { getNumberLocale } from "./numbers";

const UNIT_SUFFIXES: Record<string, { h: string; m: string; s: string; ms: string }> = {
  en: { h: "h", m: "m", s: "s", ms: "ms" },
  ar: { h: "س", m: "د", s: "ث", ms: "جزء" },
};

const getUnitSuffixes = () => UNIT_SUFFIXES[i18n.language] ?? UNIT_SUFFIXES.en;

function formatTimeFromUnit(value: number, unit: TimeUnit = "s", exclude: TimeUnit[] = ["ms"]): string {
  let totalMs = value;
  if (unit === "ms") totalMs = value;
  if (unit === "s")  totalMs = value * 1000;
  if (unit === "m")  totalMs = value * 60 * 1000;
  if (unit === "h")  totalMs = value * 3600 * 1000;

  const hrs  = Math.floor(totalMs / 3600000);
  const mins = Math.floor((totalMs % 3600000) / 60000);
  const secs = Math.floor((totalMs % 60000) / 1000);
  const ms   = Math.floor(totalMs % 1000);

  const locale = getNumberLocale();
  const localize = (n: number) => n.toLocaleString(locale);
  const suffixes = getUnitSuffixes();

  const parts: string[] = [];
  if (hrs  > 0 && !exclude.includes("h"))  parts.push(`${localize(hrs)}${suffixes.h}`);
  if (mins > 0 && !exclude.includes("m"))  parts.push(`${localize(mins)}${suffixes.m}`);
  if (secs > 0 && !exclude.includes("s"))  parts.push(`${localize(secs)}${suffixes.s}`);
  if (!exclude.includes("ms") && (ms > 0 || parts.length === 0)) parts.push(`${localize(ms)}${suffixes.ms}`);

  return parts.join(" ");
}

const toDayjs = (value: string | Dayjs): Dayjs =>
  dayjs.isDayjs(value) ? value : dayjs(value);

// dayjs instances snapshot their locale at creation time and don't follow later
// i18n.changeLanguage() calls, so formatting must always re-apply the current
// locale explicitly rather than relying on whatever locale the instance already has.
function formatDate(value?: string | Dayjs | null, format = "YYYY-MM-DD"): string {
  if (!value) return "";
  const date = toDayjs(value).locale(getDayjsLocale(i18n.language));
  if (!date.isValid()) return "";
  return date.format(format);
}

export {formatTimeFromUnit, toDayjs, formatDate}